/**
 * ScoringMathBreakdown — shows the operator exactly how a numerical
 * score was computed.
 *
 * The user's ask: "we want to see how we used [the research]
 * mathematically to create a numerical value... so we can see areas
 * for improvement, areas left out, and how we can predict more
 * successfully."
 *
 * Displays:
 *  1. Per-criterion / per-dimension contribution table (score × weight = pts)
 *  2. Sum-to-overall reconciliation
 *  3. Stacked horizontal bar visualizing each component's share
 *  4. Aggregated data gaps + improvement levers
 *
 * Used by both the rubric panel (criteria) and the match detail page
 * (dimensions). Both feed in the same shape: list of { key, label,
 * score, weight, evidence?, data_gaps? }.
 */
import React from 'react';
import { Calculator, AlertTriangle } from 'lucide-react';

export interface ScoreComponent {
  key: string;
  label: string;
  score: number | null;
  weight: number;
  evidence?: string[];
  data_gaps?: string[];
  // Marks a dimension as not-applicable (e.g. instrument for Company
  // targets). Re-weighting note is shown to the operator.
  notApplicable?: boolean;
}

interface Props {
  title?: string;
  components: ScoreComponent[];
  overallScore: number;
  // When set, we'll display "max achievable if gaps are filled = X"
  // computed by treating any 0-scored gap as if it were 100. Useful
  // to show the operator the upside.
  showMaxPotential?: boolean;
}

const colorBg = (score: number): string => {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-emerald-400';
  if (score >= 40) return 'bg-yellow-400';
  if (score >= 20) return 'bg-orange-400';
  return 'bg-red-400';
};

const colorText = (score: number): string => {
  if (score >= 60) return 'text-emerald-700';
  if (score >= 40) return 'text-yellow-700';
  return 'text-red-700';
};


const ScoringMathBreakdown: React.FC<Props> = ({
  title = 'How we computed this score',
  components,
  overallScore,
  showMaxPotential = true,
}) => {
  // Filter out N/A components and re-normalize their weights so the
  // numbers in the visible table actually sum to overallScore. Mirrors
  // the backend behaviour (instrument dropped on Company targets).
  const active = components.filter((c) => !c.notApplicable && c.score !== null);
  const droppedNA = components.filter((c) => c.notApplicable);
  const totalActiveWeight = active.reduce((s, c) => s + c.weight, 0) || 1;

  const rows = active.map((c) => {
    const s = c.score ?? 0;
    const normalizedWeight = c.weight / totalActiveWeight;
    const contribution = s * normalizedWeight;
    return {
      ...c,
      score: s,
      normalizedWeight,
      contribution,
    };
  });

  const computedTotal = Math.round(rows.reduce((s, r) => s + r.contribution, 0));

  // Max possible if every score went to 100 (i.e. all gaps filled).
  const maxPotential = 100;
  // Per-component upside = (100 - score) × normalized weight.
  const upsideRows = rows
    .map((r) => ({
      key: r.key,
      label: r.label,
      upside: (100 - r.score) * r.normalizedWeight,
      score: r.score,
    }))
    .filter((u) => u.upside > 1)
    .sort((a, b) => b.upside - a.upside);

  // Aggregate data gaps from all rows.
  const allGaps: Array<{ from: string; gap: string }> = [];
  rows.forEach((r) => {
    (r.data_gaps || []).forEach((g) => allGaps.push({ from: r.label, gap: g }));
  });

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Calculator className="w-4 h-4 text-indigo-500" />
          {title}
        </h3>
        <div className="text-right">
          <div className="text-2xl font-semibold text-gray-900">
            {Math.round(overallScore)}
            <span className="text-sm text-gray-400 font-normal"> / 100</span>
          </div>
          {Math.abs(computedTotal - overallScore) > 1 && (
            <div className="text-[10px] text-amber-600">
              (recomputed locally: {computedTotal} — slight rounding diff vs server)
            </div>
          )}
        </div>
      </div>

      {/* Stacked bar: each component's contribution as a colored slice */}
      <div className="space-y-1.5">
        <div className="flex w-full h-3 rounded overflow-hidden bg-gray-100">
          {rows.map((r) => (
            <div
              key={r.key}
              className={colorBg(r.score)}
              style={{ width: `${r.contribution}%` }}
              title={`${r.label}: ${r.score} × ${(r.normalizedWeight * 100).toFixed(0)}% = ${r.contribution.toFixed(1)} pts`}
            />
          ))}
        </div>
        <div className="text-[10px] text-gray-500">
          Each colored slice = score × (re-normalized) weight contribution
        </div>
      </div>

      {/* Per-component table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium pb-1.5">Component</th>
              <th className="text-right font-medium pb-1.5 w-16">Score</th>
              <th className="text-right font-medium pb-1.5 w-16">Weight</th>
              <th className="text-right font-medium pb-1.5 w-20">= Contrib.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="py-1.5 text-gray-700">{r.label}</td>
                <td className={`py-1.5 text-right font-medium ${colorText(r.score)}`}>
                  {r.score}
                </td>
                <td className="py-1.5 text-right text-gray-500">
                  {(r.normalizedWeight * 100).toFixed(0)}%
                </td>
                <td className="py-1.5 text-right font-medium text-gray-900">
                  {r.contribution.toFixed(1)}
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50">
              <td className="pt-1.5 font-semibold text-gray-700">Total</td>
              <td colSpan={2} className="pt-1.5"></td>
              <td className="pt-1.5 text-right font-semibold text-gray-900">
                {computedTotal} / 100
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {droppedNA.length > 0 && (
        <div className="text-xs text-gray-500 italic">
          Excluded as N/A: {droppedNA.map((d) => d.label).join(', ')} — remaining
          weights re-normalized to sum to 100%.
        </div>
      )}

      {/* Improvement levers */}
      {showMaxPotential && upsideRows.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
            Where to improve (sorted by points unlocked)
          </h4>
          <ul className="space-y-1 text-xs">
            {upsideRows.slice(0, 5).map((u) => (
              <li key={u.key} className="flex items-center justify-between text-gray-700">
                <span>{u.label}</span>
                <span className="text-gray-500">
                  +{u.upside.toFixed(1)} pts available (now: {u.score})
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[10px] text-gray-400">
            Max achievable with the current weight model: {maxPotential} / 100
          </div>
        </div>
      )}

      {/* Aggregated data gaps */}
      {allGaps.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            Data gaps that limit confidence
          </h4>
          <ul className="space-y-1 text-xs">
            {allGaps.slice(0, 8).map((g, i) => (
              <li key={i} className="text-gray-700">
                <span className="text-gray-500">[{g.from}]</span> {g.gap}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default ScoringMathBreakdown;
